import React, { useEffect, useState } from 'react';
import { Button, Spinner, MasterFormatAutocomplete } from '../../../shared/components/ui';
import { formatCurrency, formatUnitCost } from '../../../shared/utils/formatters';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { useToast } from '../../../shared/context/ToastContext';

// Full-screen sheet for adding/editing a single line item on phones.
// Desktop keeps the inline table editor; this only renders when explicitly
// opened (always rendered conditionally by the parent), and is sized to fill
// the viewport so users get one focused form at a time.
//
// Props:
//   item               existing line item (or seed with description='New Line Item')
//   isNew              true if this is a brand-new item being added
//   isCustomMode       sub-trade tenants: free-text description, no CSI picker
//   categoryCode       category masterformat code (used for fee-row handling)
//   categoryName       human-readable category name
//   divisionNumber     restricts MasterFormat picker to this division
//   estimateId         needed for AI estimation API call
//   projectContext     passed to AI estimation
//   excludeEstimateId  prevents AI from referencing the current estimate
//   existingLineItemCodes other items in same category, so picker excludes dupes
//   onSave(updatedItem) parent persists the merged item
//   onCancel()         dismiss without saving (parent also deletes if isNew)
//   onClose()          dismiss (called after save too)
export function MobileLineItemSheet({
  item,
  isNew = false,
  isCustomMode = false,
  categoryCode,
  categoryName,
  divisionNumber,
  estimateId,
  projectContext,
  excludeEstimateId,
  existingLineItemCodes = [],
  onSave,
  onCancel,
  onClose,
}) {
  const { showError } = useToast();
  const isFeeCategory = categoryCode === '00';

  const [description, setDescription] = useState(
    item.description && item.description !== 'New Line Item' ? item.description : ''
  );
  const [selectedMasterFormat, setSelectedMasterFormat] = useState(
    item.masterformatCode
      ? { code: item.masterformatCode, title: item.description }
      : null
  );
  const [quantity, setQuantity] = useState(item.quantity != null ? String(item.quantity) : '');
  const [unit, setUnit] = useState(item.unit || '');
  const [unitCost, setUnitCost] = useState(item.unitCost != null ? String(item.unitCost) : '');
  const [totalCost, setTotalCost] = useState(item.totalCost != null ? String(item.totalCost) : '');
  // 'unitCost' (default): total auto-derives from qty * unitCost.
  // 'total': user types total directly; unitCost back-calculates from qty.
  const [costMode, setCostMode] = useState('unitCost');
  const [error, setError] = useState('');
  const [aiLoading, setAILoading] = useState(false);

  // Lock background scroll while sheet is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes (sheet is treated like a modal).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Derived preview values so the user can see what the total will be.
  const previewTotal = isFeeCategory
    ? (totalCost ? `${totalCost}%` : '—')
    : (() => {
        if (costMode === 'total') return totalCost ? formatCurrency(parseFloat(totalCost)) : '—';
        const q = parseFloat(quantity);
        const u = parseFloat(unitCost);
        return q && u ? formatCurrency(q * u) : '—';
      })();
  const previewUnitCost = !isFeeCategory && costMode === 'total' && quantity && totalCost
    ? formatUnitCost(parseFloat(totalCost) / parseFloat(quantity))
    : (unitCost ? formatUnitCost(parseFloat(unitCost)) : '—');

  const handleSave = () => {
    // Validate.
    if (!isCustomMode && !selectedMasterFormat?.code) {
      setError('Pick a MasterFormat code for this line item.');
      return;
    }
    if (isCustomMode && !description.trim()) {
      setError('Describe this line item.');
      return;
    }
    if (isFeeCategory) {
      if (!totalCost || isNaN(parseFloat(totalCost))) {
        setError('Enter a fee percentage.');
        return;
      }
    } else {
      const q = parseFloat(quantity);
      if (!q || isNaN(q)) {
        setError('Enter a quantity.');
        return;
      }
      if (costMode === 'unitCost') {
        const u = parseFloat(unitCost);
        if (!u || isNaN(u)) {
          setError('Enter a unit cost (or switch to total).');
          return;
        }
      } else {
        const t = parseFloat(totalCost);
        if (!t || isNaN(t)) {
          setError('Enter a total cost (or switch to unit cost).');
          return;
        }
      }
    }
    setError('');

    // Build payload — same shape the inline editor produces.
    let q, u, t;
    if (isFeeCategory) {
      t = parseFloat(totalCost);
      q = null;
      u = null;
    } else {
      q = parseFloat(quantity);
      if (costMode === 'total') {
        t = parseFloat(totalCost);
        u = q ? t / q : null;
      } else {
        u = parseFloat(unitCost);
        t = q * u;
      }
    }

    const resolvedDescription = isCustomMode
      ? description.trim()
      : (selectedMasterFormat?.title || description.trim() || item.description);
    const resolvedCode = isCustomMode ? item.masterformatCode : selectedMasterFormat?.code;

    onSave({
      ...item,
      description: resolvedDescription,
      masterformatCode: resolvedCode,
      quantity: q,
      unit: isFeeCategory ? null : (unit || null),
      unitCost: u,
      totalCost: t,
      costSource: isNew
        ? 'manual'
        : (item.costSource === 'ai' && t !== item.totalCost ? 'user_adjusted' : item.costSource || 'manual'),
    });
    onClose();
  };

  const handleAIEstimate = async () => {
    const code = selectedMasterFormat?.code || item.masterformatCode;
    const desc = selectedMasterFormat?.title || description || item.description;
    if (!code) {
      setError('Pick a MasterFormat code before requesting an AI estimate.');
      return;
    }
    setError('');
    setAILoading(true);
    try {
      const response = await estimatesApi.estimateLineItems(
        projectContext || {},
        [{
          description: desc,
          categoryName: categoryName || 'General',
          masterformatCode: code,
          quantity: quantity ? parseFloat(quantity) : null,
          unit: unit || null,
        }],
        excludeEstimateId
      );
      const est = response.estimates?.[0] || response[0];
      if (est) {
        if (est.quantity != null) setQuantity(String(est.quantity));
        if (est.unit) setUnit(est.unit);
        if (est.unitCost != null) {
          setUnitCost(String(est.unitCost));
          setCostMode('unitCost');
        }
      }
    } catch (err) {
      console.error('AI estimate failed:', err);
      showError('AI estimate failed. Please try again or enter values manually.');
    } finally {
      setAILoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={isNew ? 'Add line item' : 'Edit line item'}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} aria-hidden="true" />

      {/* Sheet — full-screen on phones */}
      <div className="relative ml-auto w-full h-full bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isNew ? 'Add Line Item' : 'Edit Line Item'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="-mr-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {/* Item identifier */}
          {isCustomMode ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this line item"
                className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                MasterFormat Item <span className="text-red-500">*</span>
              </label>
              <MasterFormatAutocomplete
                value={selectedMasterFormat}
                onChange={(selected) => {
                  setSelectedMasterFormat(selected);
                  if (selected) setError('');
                }}
                placeholder="Search or pick an item..."
                divisionFilter={divisionNumber}
                showInitialList={true}
                showDivisionHeaders={false}
                excludeCodes={existingLineItemCodes}
              />
              {selectedMasterFormat?.code && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                  {selectedMasterFormat.code}
                </p>
              )}
            </div>
          )}

          {/* Fee categories collapse to a single % field. */}
          {isFeeCategory ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Fee Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  placeholder="e.g. 10"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg pl-3 pr-8 py-2.5 text-base text-right focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <span className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0"
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2.5 text-base text-right focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="EA, SF, LF..."
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Cost mode toggle + the chosen input */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {costMode === 'unitCost' ? 'Unit Cost' : 'Total Cost'}
                  </label>
                  <button
                    type="button"
                    onClick={() => setCostMode(costMode === 'unitCost' ? 'total' : 'unitCost')}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Enter {costMode === 'unitCost' ? 'total' : 'unit cost'} instead
                  </button>
                </div>
                {costMode === 'unitCost' ? (
                  <input
                    type="number"
                    inputMode="decimal"
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    placeholder="$ per unit"
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2.5 text-base text-right focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                ) : (
                  <input
                    type="number"
                    inputMode="decimal"
                    value={totalCost}
                    onChange={(e) => setTotalCost(e.target.value)}
                    placeholder="$ total"
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2.5 text-base text-right focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                )}
              </div>
            </>
          )}

          {/* Live preview */}
          {!isFeeCategory && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm">
              <div className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>Unit cost</span>
                <span className="font-medium">{previewUnitCost}</span>
              </div>
              <div className="flex justify-between text-gray-900 dark:text-gray-100 mt-1">
                <span className="font-medium">Total</span>
                <span className="font-bold">{previewTotal}</span>
              </div>
            </div>
          )}

          {/* AI estimate button (skipped for fee + custom-mode rows). */}
          {!isFeeCategory && !isCustomMode && (
            <button
              type="button"
              onClick={handleAIEstimate}
              disabled={aiLoading}
              className="w-full flex items-center justify-center gap-2 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
            >
              {aiLoading ? (
                <>
                  <Spinner size="sm" /> Asking AI…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Get AI estimate
                </>
              )}
            </button>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer — sticky action bar */}
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3 bg-white dark:bg-gray-900 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSave}>
            {isNew ? 'Add' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MobileLineItemSheet;
